import { Inject, Injectable } from '@angular/core';
import { NgxLoggerService } from '@go-modules/services/logger/logger.service';
import { FullstoryService, fullstoryToken } from 'go-modules/services/fullstory/fullstory.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { ResourceTypes } from '@go-modules/enums/archive';

export interface UploadContext {
	user_id?: string;
	activity_id?: string;
	library_collection_id?: string;
	session_id?: string;
	group_id?: string;
	resource_type?: string;
	resource_id?: string;
	media_id?: string;
}

export interface UploadTrackingData {
	startTime: number;
	lastUploadedBytes: number;
	context?: UploadContext;
}

@Injectable({
	providedIn: 'root'
})
export class NgxUploadTrackerService {
	private uploadData = new Map<number, UploadTrackingData>();
	private contextOverride: UploadContext | null = null;

	constructor (
		private logger: NgxLoggerService,
		@Inject(fullstoryToken) private fullstoryService: FullstoryService
	) {}

	public setUploadContext (context: UploadContext): void {
		this.contextOverride = context;
	}

	public updateContextWithUMCOptions (umcOptions): void {
		const detectedContext = this.detectUploadContext(umcOptions);
		this.setUploadContext(detectedContext);
	}

	public clearUploadContext (): void {
		this.contextOverride = null;
	}

	public trackUploadStart (fileId: number, context?: UploadContext): void {
		this.uploadData.set(fileId, {
			startTime: Date.now(),
			lastUploadedBytes: 0,
			context: context || this.contextOverride || {}
		});
	}

	public updateProgress (fileId: number, uploadedBytes: number): void {
		const data = this.uploadData.get(fileId);
		if (data) {
			data.lastUploadedBytes = uploadedBytes;
		}
	}

	public getElapsedTime (fileId: number): number {
		const data = this.uploadData.get(fileId);
		if (!data) return 0;

		return (Date.now() - data.startTime) / 1000;
	}

	public getAmountUploaded (fileId: number): number {
		const data = this.uploadData.get(fileId);
		return data ? data.lastUploadedBytes : 0;
	}

	public getUploadContext (fileId?: number): UploadContext {
		const data = this.uploadData.get(fileId);
		return data?.context || this.contextOverride || {};
	}

	public cleanupUpload (fileId: number): void {
		this.uploadData.delete(fileId);
	}

	public logUploadFailure (file: any, fileId: number, errorReason: string): void {
		const context = this.getUploadContext(fileId);
		const elapsedTime = this.getElapsedTime(fileId);
		const amountUploaded = this.getAmountUploaded(fileId);

		const logData = {
			status: 'failed',
			file_name: file.name,
			file_type: this.getFileType(file.name),
			file_size: file.size,
			elapsed_time: elapsedTime,
			amount_uploaded: amountUploaded,
			error_type: 'upload_failed',
			error_reason: errorReason,
			context
		};

		this.logger.logError('UPLOAD_FAILURE', logData);

		this.fullstoryService.createEvent(FULLSTORY_EVENTS.UPLOAD_ERROR, {
			error_type: 'upload_failed',
			file_name: file.name,
			error_reason: errorReason,
			...context
		});

		this.cleanupUpload(fileId);
	}

	public logUploadCanceled (file: any, fileId: number, wasUploading: boolean): void {
		const context = this.getUploadContext(fileId);
		const elapsedTime = wasUploading ? this.getElapsedTime(fileId) : 0;
		const amountUploaded = wasUploading ? this.getAmountUploaded(fileId) : 0;

		const logData = {
			status: 'canceled',
			file_name: file.name,
			file_type: this.getFileType(file.name),
			file_size: file.size,
			elapsed_time: elapsedTime,
			amount_uploaded: amountUploaded,
			error_type: 'upload_cancelled',
			context
		};

		this.logger.logError('UPLOAD_CANCELED', logData);

		this.fullstoryService.createEvent(FULLSTORY_EVENTS.UPLOAD_ERROR, {
			error_type: 'upload_cancelled',
			file_name: file.name,
			...context
		});

		this.cleanupUpload(fileId);
	}

	public logValidationFailure (file: any, errorType: string, additionalData?: any): void {
		const context = this.contextOverride || {};

		const logData = {
			status: 'validation_failed',
			file_name: file.name,
			file_type: this.getFileType(file.name),
			file_size: file.size,
			elapsed_time: 0,
			amount_uploaded: 0,
			error_type: errorType,
			context,
			...additionalData
		};

		this.logger.logError('UPLOAD_VALIDATION_FAILURE', logData);

		this.fullstoryService.createEvent(FULLSTORY_EVENTS.UPLOAD_ERROR, {
			error_type: errorType,
			file_name: file.name,
			file_type: this.getFileType(file.name),
			...additionalData,
			...context
		});
	}

	private getFileType (fileName: string): string {
		const parts = fileName.split('.');
		if (parts.length <= 1) return 'unknown';
		return parts.pop().toLowerCase();
	}

	private detectUploadContext (umcOptions): UploadContext {
		const context: UploadContext = {
			group_id: umcOptions?.groupId?.toString()
		};

		// Process UMC options first
		if (umcOptions?.resourceType && umcOptions?.resourceId) {
			context.resource_type = umcOptions.resourceType;
			context.resource_id = umcOptions.resourceId.toString();

			if (umcOptions.resourceType === ResourceTypes.ACTIVITY) {
				context.activity_id = umcOptions.resourceId.toString();
			} else if (umcOptions.resourceType === ResourceTypes.SESSION) {
				context.session_id = umcOptions.resourceId.toString();
			} else if (umcOptions.resourceType === ResourceTypes.GROUP) {
				context.group_id = umcOptions.resourceId.toString();
			}
		} else if (umcOptions?.addToCollection) {
			context.resource_type = 'library';
			context.library_collection_id = umcOptions.addToCollection.toString();
		} else {
			context.resource_type = 'unknown';
		}

		// Then merge with context override
		if (this.contextOverride) {
			return {
				...this.contextOverride,
				...context, // UMC options take precedence
				group_id: context.group_id || this.contextOverride.group_id
			};
		}

		return context;
	}

	public logUploadSuccess (file: any, fileId: number): void {
		const context = this.getUploadContext(fileId);
		const elapsedTime = this.getElapsedTime(fileId);
		const amountUploaded = this.getAmountUploaded(fileId);

		const logData = {
			status: 'succeeded',
			file_name: file.name,
			file_type: this.getFileType(file.name),
			file_size: file.size,
			elapsed_time: elapsedTime,
			amount_uploaded: amountUploaded || file.size, // Use file.size if no progress tracked
			context
		};

		this.logger.logWarn('UPLOAD_SUCCESS', logData);
		this.cleanupUpload(fileId);
	}
}
